import Project from '@theme/global-functions';
import { enableFloatingHeader } from './fixed';

class HeaderBlock {
  constructor(element) {
    this.header = element;
    this.mobileBreakpoint = this.header.dataset.mobileBreakpoint || 'lg';
    this.mobileMenuToggle = this.header.querySelector(
      '.header-block__toggle-button'
    );
    this.mobileMenuClose = this.header.querySelector(
      '.header-block__menu-close'
    );
    this.mobileMenu = this.header.querySelector('.mobile-menu');
    this.mobileMenuOverlay = this.header.querySelector('.mobile-menu-overlay');
    this.desktopContent = this.header.querySelector('.header-desktop-content');
    this.mobileMenuContent = this.header.querySelector('.mobile-menu-content');
    this.mobileMenuDefault = this.header.querySelector('.mobile-menu-default');
    this.isSticky = this.header.classList.contains('sticky-header');
    this.breakpointMap = {
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1400,
    };
    this.resizeTimer = null;
    this.scrollTimer = null;
    this.hamburgerIcon = this.mobileMenuToggle
      ? this.mobileMenuToggle.querySelector('.hamburger-icon')
      : null;
  }

  init() {
    if (!this.header) return;

    this.setupEventListeners();
    this.setupStickyHeader();

    this.handleResize();

    if (this.mobileMenu) {
      this.mobileMenu.setAttribute('aria-hidden', 'true');
    }

    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    }
  }

  setupEventListeners() {
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.addEventListener(
        'click',
        this.toggleMobileMenu.bind(this)
      );
    }

    if (this.mobileMenuClose) {
      this.mobileMenuClose.addEventListener(
        'click',
        this.closeMobileMenu.bind(this)
      );
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.addEventListener(
        'click',
        this.closeMobileMenu.bind(this)
      );
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMobileMenuOpen()) {
        this.closeMobileMenu();
      }
    });

    window.addEventListener('resize', () => {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.handleResize.bind(this), 100);
    });
  }

  setupStickyHeader() {
    if (!this.isSticky) return;

    const scrollHandler = () => {
      if (this.scrollTimer) clearTimeout(this.scrollTimer);

      this.scrollTimer = setTimeout(() => {
        if (window.scrollY > 10) {
          this.header.classList.add('scrolled');
        } else {
          this.header.classList.remove('scrolled');
        }
      }, 10);
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    scrollHandler();
  }

  handleResize() {
    const breakpointValue = this.breakpointMap[this.mobileBreakpoint] || 1520;
    const isMobile = window.innerWidth < breakpointValue;

    if (isMobile) {
      this.header.classList.add('mobile-view');
      this.header.classList.remove('desktop-view');
    } else {
      this.header.classList.remove('mobile-view');
      this.header.classList.add('desktop-view');
      this.closeMobileMenu();
    }

    if (this.mobileMenu) {
      this.mobileMenu.style.maxHeight = `${window.innerHeight}px`;
    }
  }

  toggleMobileMenu() {
    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  openMobileMenu() {
    this.header.classList.add('mobile-menu-opening');
    this.setupFocusTrap();

    setTimeout(() => {
      this.header.classList.add('mobile-menu-open');
      this.header.classList.remove('mobile-menu-opening');
      document.body.classList.add('mobile-menu-active');
      document.body.style.overflow = 'hidden'; // Prevent page scrolling
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      this.mobileMenuToggle.setAttribute('aria-expanded', 'true');
    }, 10);
  }

  closeMobileMenu() {
    this.header.classList.add('mobile-menu-closing');
    this.header.classList.remove('mobile-menu-open');

    this.removeFocusTrap();

    setTimeout(() => {
      this.header.classList.remove('mobile-menu-closing');
      document.body.classList.remove('mobile-menu-active');
      document.body.style.overflow = '';
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    }, 300);
  }

  setupFocusTrap() {
    if (!this.mobileMenu) return;

    const focusableElements = this.mobileMenu.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    firstElement.focus();

    this.mobileMenu.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  removeFocusTrap() {
    if (!this.mobileMenu) return;

    this.mobileMenu.removeEventListener('keydown', () => {});

    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.focus();
    }
  }

  isMobileMenuOpen() {
    return this.header.classList.contains('mobile-menu-open');
  }
}

Project.ready(() => {
  // enableFloatingHeader();
  document.querySelectorAll('.header-block').forEach((module) => {
    const instance = new HeaderBlock(module);
    instance.init();
    module.headerBlockInstance = instance;
  });
});

export default HeaderBlock;
